/**
 * Icônes de la barre latérale.
 *
 * SVG en ligne, 20 px, trait `currentColor` : elles prennent la couleur du
 * lien (secondaire, ou cyan quand la page est active) sans jeu de couleurs à
 * elles. Pas de bibliothèque d'icônes pour onze pictogrammes.
 *
 * Décoratives (`aria-hidden`) : le libellé à côté porte le sens.
 */

import type { ReactNode } from 'react';

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      className="sidebar__icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export const NAV_ICONS = {
  summary: (
    <Icon>
      <rect x="3.5" y="3.5" width="17" height="17" rx="2" />
      <path d="M8 16v-3M12 16V8M16 16v-5" />
    </Icon>
  ),
  markers: (
    <Icon>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
      <rect x="7" y="13" width="3" height="3" rx="0.5" />
    </Icon>
  ),
  shopify: (
    <Icon>
      <path d="M4 9.5 5.5 4h13L20 9.5" />
      <path d="M4 9.5a2.7 2.7 0 0 0 5.3 0 2.7 2.7 0 0 0 5.4 0 2.7 2.7 0 0 0 5.3 0" />
      <path d="M5.5 12v8h13v-8" />
    </Icon>
  ),
  subscriptions: (
    <Icon>
      <path d="M12.6 3.5H20v7.4l-8.7 8.7a1.5 1.5 0 0 1-2.1 0l-5.3-5.3a1.5 1.5 0 0 1 0-2.1z" />
      <circle cx="16" cy="8" r="1.3" />
    </Icon>
  ),
  amazon: (
    <Icon>
      <path d="M3 4h2.5l2.2 10.5h10.6L20.5 7H7" />
      <circle cx="9.5" cy="19" r="1.4" />
      <circle cx="17" cy="19" r="1.4" />
    </Icon>
  ),
  btob: (
    <Icon>
      <path d="M4 10.5 12 4l8 6.5V20H4z" />
      <path d="M10 20v-5.5h4V20" />
    </Icon>
  ),
  ads: (
    <Icon>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M14.7 9.2c-.4-.9-1.5-1.5-2.7-1.5-1.6 0-2.8.9-2.8 2.1 0 2.8 5.8 1.4 5.8 4.3 0 1.2-1.3 2.2-3 2.2-1.3 0-2.5-.6-2.9-1.6M12 6v1.7M12 16.3V18" />
    </Icon>
  ),
  fees: (
    <Icon>
      <rect x="3.5" y="3.5" width="7" height="9" rx="1" />
      <rect x="13.5" y="3.5" width="7" height="5" rx="1" />
      <rect x="13.5" y="11.5" width="7" height="9" rx="1" />
      <rect x="3.5" y="15.5" width="7" height="5" rx="1" />
    </Icon>
  ),
  charges: (
    <Icon>
      <rect x="3.5" y="3.5" width="7" height="5" rx="1" />
      <rect x="13.5" y="3.5" width="7" height="9" rx="1" />
      <rect x="3.5" y="11.5" width="7" height="9" rx="1" />
      <rect x="13.5" y="15.5" width="7" height="5" rx="1" />
    </Icon>
  ),
  analytics: (
    <Icon>
      <path d="M4 4h16M5 4v4.5a2.3 2.3 0 0 0 4.7 0 2.3 2.3 0 0 0 4.6 0 2.3 2.3 0 0 0 4.7 0V4" />
      <path d="M5.5 12v8h13v-8M10 20v-4h4v4" />
    </Icon>
  ),
  usage: (
    <Icon>
      <rect x="6.5" y="3" width="11" height="18" rx="2" />
      <path d="M9.5 15v3M12 15v3M14.5 15v3M8.5 6.5h7" />
    </Icon>
  ),
  account: (
    <Icon>
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" />
    </Icon>
  ),
  signout: (
    <Icon>
      <path d="M9 4H5.5A1.5 1.5 0 0 0 4 5.5v13A1.5 1.5 0 0 0 5.5 20H9M15 16l4-4-4-4M19 12H9" />
    </Icon>
  ),
  support: (
    <Icon>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.5 14c.8 1.3 2 2 3.5 2s2.7-.7 3.5-2" />
      <path d="M9 9.5h.01M15 9.5h.01" strokeWidth="2.6" />
    </Icon>
  ),
} as const;

export type NavIconName = keyof typeof NAV_ICONS;
